import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Divider, Icon } from "semantic-ui-react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { setUpdateDocuments } from "../../../../../redux/actions/documentostributarios";
import { formatMoneyWithCurrency } from "../../../../../utils/formatter";
import {
  getReconciledAmount,
  isFullyReconciled,
} from "../../../../../utils/reconciliationProgress";
import moment from "moment";
import { FindMatchsByTransactionId } from "../../../../../graphql/conciliacion";
import { FindDocumentCandidates } from "../../../../../graphql/documents";
import DocumentInfoBar from "./documentInfoBar";
import VincularDocumentoModal from "./VincularDocumentoModal";
import CreateObligationModal from "../../../../configuracion/createObligationModal/createObligationModal";
import MatchList from "./components/MatchList";
import AIMovementSuggestion from "./components/AIMovementSuggestion";
import PaymentMethodDropdown from "./components/PaymentMethodDropdown";
import DocumentActionsDropdown from "./components/DocumentActionsDropdown";
import { getDocumentLabelWithNumber, getName, getMatchUserInfo } from "./components/matchRowHelpers";
import {
  buildObligationCopyInitialData,
  canCopyObligationDocument,
  isObligationDocument,
} from "../../../../configuracion/createObligationModal/obligationCopyHelpers";
import ModalDeleteObligations from "../../../../documentostributarios/ModalDeleteObligations";
import CustomModal from "../../../../configuracion/components/customModal";
import UndoAllMatchesModalContent from "./components/UndoAllMatchesModalContent";
import { useUndoAllMatches } from "./hooks/useUndoAllMatches";
import BulkUnmatchProgress from "./BulkUnmatchProgress";
import { dt } from "src/utils/documentsI18n";
import { importOpenSidebar } from "../../../../../redux/actions/openSidebar";
import ReportDocumentButton from "../../../../shared/ReportDocumentButton/ReportDocumentButton";
import { useBlockedFeatures } from "src/hooks/useBlockedFeatures";
import { buildUnmatchInput } from "./utils/unmatchInput";
import { useStatementUnmatchGuard } from "./hooks/useStatementUnmatchGuard";
import StatementLinkedUnmatchModal from "./components/StatementLinkedUnmatchModal";
import { getObligationTypeForPaymentMethod } from "src/utils/paymentMethodObligationType";
import "./style.scss";
import "./reconcileComments.css";

// Helper function to get match description based on origin
const getMatchDescription = (match) => {
  if (match.origin === 'clay') {
    return 'Match realizado en Clay';
  } else if (match.origin === 'manual') {
    return 'Match realizado manualmente';
  }
  return 'Match realizado manualmente'; // default
};

/**
 * ReconcileDocumentSideBar - Reconcile component for documents/obligations
 * Similar to ReconcileSideBar but specifically for documents
 * Uses DocumentInfoBar instead of TransactionInfoBar
 * Makes only 2 API calls: FIND_DOCUMENT_BY_ID (from reconcile.jsx) and FIND_MATCHS_BY_TRANSACTION_ID
 */
const ReconcileDocumentSideBar = ({ hideActionsAndMatches = false, hideReportButton = false, documentData = null }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const companyId = selectedCompany?.id;
  const { isConciliacionBlocked, isCrearObligacionBlocked, isAbonarBlocked } = useBlockedFeatures();
  const isReadOnly = isConciliacionBlocked();
  const crearObligacionBlocked = isCrearObligacionBlocked();
  const abonarBlocked = isAbonarBlocked();
  
  // Use documentData prop if provided (from modal context), otherwise use Redux
  const editData = documentData || openSidebar?.editData;
  const { options } = openSidebar;

  // Modal states
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [showAbonoObligationModal, setShowAbonoObligationModal] = useState(false);
  const [showCopyObligationModal, setShowCopyObligationModal] = useState(false);
  const [showDeleteObligationModal, setShowDeleteObligationModal] = useState(false);
  const [showUndoAllModal, setShowUndoAllModal] = useState(false);
  const [preSelectedObligationType, setPreSelectedObligationType] = useState(null);
  const [isPaymentMethod, setIsPaymentMethod] = useState(false);

  const showObligationActions = useMemo(() => isObligationDocument(editData), [editData]);
  const showObligationCopyAction = useMemo(
    () => canCopyObligationDocument(editData),
    [editData],
  );
  const obligationDeleteDocuments = useMemo(
    () => (editData ? [editData] : []),
    [editData],
  );
  const copyObligationInitialData = useMemo(
    () => (showCopyObligationModal ? buildObligationCopyInitialData(editData) : null),
    [showCopyObligationModal, editData],
  );

  /** Cierra el panel interno (misma lógica que TablaDocumentos.handleCloseReconcile). */
  const closeInternalPanel = useCallback(() => {
    const currentParams = new URLSearchParams(location.search);
    const removedDocumentId = currentParams.get('documentId');
    currentParams.delete('documentId');
    const listId = currentParams.get('id');
    if (removedDocumentId && listId === removedDocumentId) {
      currentParams.delete('id');
    }
    history.replace({ search: currentParams.toString() });
    dispatch(
      importOpenSidebar({
        show: false,
        reconcile: false,
        editData: null,
      }),
    );
  }, [dispatch, history, location.search]);

  // Fetch matches using FindMatchsByTransactionId with dte: true for documents
  // This is the only API call made in this component (FIND_MATCHS_BY_TRANSACTION_ID)
  const documentId = editData?._id || editData?.id;
  const { data: matchesData, fetching: fetchingMatches, reexecuteQuery: refetchMatches } = FindMatchsByTransactionId({
    transactionId: documentId,
    dte: true, // true means it's a document
  });

  // Candidatos rendición↔factura: query separada para no bloquear FIND_DOCUMENT_BY_ID
  const candidatesVariables = useMemo(() => {
    return documentId ? { ids: [documentId] } : { ids: [] };
  }, [documentId]);
  const { data: candidatesData } = FindDocumentCandidates(candidatesVariables);

  const rendicionStrongCandidates = useMemo(() => {
    const doc = candidatesData?.findDocumentsById?.[0];
    const candidates = doc?.strongMatchCandidates || [];
    return candidates.map(c => c.document).filter(Boolean);
  }, [candidatesData]);

  const rendicionWeakCandidates = useMemo(() => {
    const doc = candidatesData?.findDocumentsById?.[0];
    const candidates = doc?.weakMatchCandidates || [];
    return candidates.map(c => c.document).filter(Boolean);
  }, [candidatesData]);

  const aiInvoiceMovementSuggestion = useMemo(() => {
    const doc = candidatesData?.findDocumentsById?.[0];
    return doc?.aiInvoiceMovementMatchSuggestion || null;
  }, [candidatesData]);

  // Force refetch when documentId changes
  useEffect(() => {
    if (documentId) {
      refetchMatches({ requestPolicy: 'network-only' });
    }
  }, [documentId, refetchMatches]);

  // Listen for document updates from Redux to refetch matches
  const { updateDocuments } = useSelector((state) => state.documentostributarios || {});
  useEffect(() => {
    if (updateDocuments?.value === true && documentId) {
      // Refetch matches when documents are updated (e.g., after creating a match)
      refetchMatches({ requestPolicy: 'network-only' });
    }
  }, [updateDocuments?.value, documentId, refetchMatches]);

  // Get transactions from allTransactionsByDocumentId - these are already full transaction objects
  const fetchedTransactions = matchesData?.allTransactionsByDocumentId || [];

  // Get documents/obligations from allMatchsByTransactionId - includes full document objects with meta
  const fetchedDocuments = matchesData?.allMatchsByTransactionId || [];

  // Get manual and clay matches from query results
  const fetchedManualMatches = matchesData?.allMatchManualByTransactionId || [];
  const fetchedClayMatches = matchesData?.allMatchByClay || [];
  
  // Filter for obligations (documents with ObligationMeta)
  const fetchedObligations = useMemo(() => {
    return fetchedDocuments.filter(doc => doc.meta && 'family' in doc.meta);
  }, [fetchedDocuments]);

  // Filter for TAX_DOCUMENT matches (documents with TaxDocumentMeta but not ObligationMeta)
  const fetchedTaxDocuments = useMemo(() => {
    return fetchedDocuments.filter(doc => {
      // Include documents that have meta but are not obligations
      return doc.meta && !('family' in doc.meta) && doc.documentType === 'TAX_DOCUMENT';
    });
  }, [fetchedDocuments]);

  // Get document matches from editData (from FIND_DOCUMENT_BY_ID call in reconcile.jsx)
  // Use documentData prop first (fresh data), fallback to editData
  const sourceData = documentData || editData;
  const matchesFromDocument = useMemo(() => {
    return [
      ...(sourceData?.meta?.matchs?.inMatch || []),
      ...(sourceData?.meta?.matchs?.outMatch || [])
    ];
  }, [sourceData]);

  // Create a map of match data by ID for enrichment (works for both transactions and obligations)
  const matchDataMap = useMemo(() => {
    return matchesFromDocument.reduce((acc, match) => {
      const matchId = match.id || match.settleId;
      if (matchId) {
        acc[matchId] = match;
      }
      return acc;
    }, {});
  }, [matchesFromDocument]);

  // Create a map of fetched documents by ID for quick lookup
  const fetchedDocumentsMap = useMemo(() => {
    return fetchedDocuments.reduce((acc, doc) => {
      if (doc.id) {
        acc[doc.id] = doc;
      }
      return acc;
    }, {});
  }, [fetchedDocuments]);

  // Enrich transactions with match-specific data (amounts, exchange rates, etc.)
  const enrichedTransactionMatches = useMemo(() => {
    return fetchedTransactions.map(transaction => {
      const matchData = matchDataMap[transaction.id];
      if (matchData) {
        // Merge match data with transaction data
        return {
          ...transaction,
          // Keep match-specific data
          matchedAmount: matchData.matchedAmount || matchData.amount || transaction.amount,
          // Preserve match userRelated if available
          userRelated: matchData.userRelated || transaction.userRelated,
          // Use match currency and amount if available
          currency: matchData.currency || transaction.currency || 'CLP',
          // Preserve exchange rate and original amount fields
          originalAmount: matchData.originalAmount,
          exchangeRate: matchData.exchangeRate,
          localValue: matchData.localValue,
          matchCurrency: matchData.matchCurrency,
          ...(matchData.entries != null ? { entries: matchData.entries } : {}),
        };
      }
      // If no match data, return transaction as-is
      return transaction;
    });
  }, [fetchedTransactions, matchDataMap]);

  // Build obligation match objects from fetched obligation documents
  const enrichedObligationMatches = useMemo(() => {
    return fetchedObligations.map(obligation => {
      const matchData = matchDataMap[obligation.id];
      if (matchData) {
        const { entries: _obligationEmissionEntries, ...obligationRest } = obligation;
        // Merge obligation document with match data
        return {
          ...obligationRest,
          // Ensure documentType is set
          documentType: obligation.documentType || 'OBLIGATION',
          // Add match-specific data
          matchedAmount: matchData.matchedAmount || matchData.amount || obligation.amount,
          userRelated: matchData.userRelated,
          currency: matchData.currency || matchData.matchCurrency || obligation.currencyType || 'CLP',
          originalAmount: matchData.originalAmount,
          exchangeRate: matchData.exchangeRate,
          localValue: matchData.localValue || matchData.matchedAmount || matchData.amount,
          matchCurrency: matchData.matchCurrency || 'CLP',
          // Preserve match source if available
          matchSource: matchData.matchSource,
          origin: matchData.origin,
          matchId: matchData.matchId ?? undefined,
          ...(matchData.entries != null ? { entries: matchData.entries } : {}),
        };
      }
      // If no match data, return obligation as-is
      return {
        ...obligation,
        documentType: obligation.documentType || 'OBLIGATION',
      };
    });
  }, [fetchedObligations, matchDataMap]);

  // Build TAX_DOCUMENT match objects from fetched tax documents
  const enrichedTaxDocumentMatches = useMemo(() => {
    return fetchedTaxDocuments.map(taxDoc => {
      const matchData = matchDataMap[taxDoc.id];
      if (matchData) {
        const { entries: _taxEmissionEntries, ...taxDocRest } = taxDoc;
        // Merge tax document with match data
        return {
          ...taxDocRest,
          // Ensure documentType is set
          documentType: taxDoc.documentType || 'TAX_DOCUMENT',
          // Add match-specific data
          matchedAmount: matchData.matchedAmount || matchData.amount || taxDoc.amount,
          userRelated: matchData.userRelated,
          currency: matchData.currency || matchData.matchCurrency || taxDoc.currencyType || 'CLP',
          originalAmount: matchData.originalAmount,
          exchangeRate: matchData.exchangeRate,
          localValue: matchData.localValue || matchData.matchedAmount || matchData.amount,
          matchCurrency: matchData.matchCurrency || 'CLP',
          // Preserve match source if available
          matchSource: matchData.matchSource,
          origin: matchData.origin,
          matchId: matchData.matchId ?? undefined,
          ...(matchData.entries != null ? { entries: matchData.entries } : {}),
        };
      }
      // If no match data, return tax document as-is
      return {
        ...taxDoc,
        documentType: taxDoc.documentType || 'TAX_DOCUMENT',
      };
    });
  }, [fetchedTaxDocuments, matchDataMap]);

  // Format manual and clay matches as simple objects with description
  const formattedManualMatches = useMemo(() => {
    // Combine both arrays and ensure clay matches have origin set
    const allManualMatches = [
      ...fetchedManualMatches,
      ...fetchedClayMatches.map(match => ({
        ...match,
        origin: match.origin || 'clay', // Ensure clay matches have origin
      }))
    ];

    return allManualMatches.map(match => {
      const matchId = match.settleId || match.id;
      const matchData = matchDataMap[matchId];
      return {
        id: matchId,
        settleId: match.settleId,
        amount: match.amount,
        description: getMatchDescription(match),
        documentDate: editData?.documentDate,
        date: editData?.documentDate,
        currencyType: match.currencyType || editData?.currencyType || 'CLP',
        type: 'MANUAL',
        origin: match.origin || 'manual',
        userRelated: match.userRelated,
        documentType: null,
        // Add match-specific fields for MatchList
        matchedAmount: match.amount,
        currency: match.currencyType || 'CLP',
        matchSource: 'manual',
        entries: matchData?.entries,
        // Add meta for compatibility with matchRowHelpers
        meta: {
          summary: getMatchDescription(match),
          isReceived: editData?.meta?.isReceived !== undefined ? editData.meta.isReceived : true,
        },
      };
    });
  }, [fetchedManualMatches, fetchedClayMatches, editData, matchDataMap]);

  // Handle matches that exist in matchesFromDocument but aren't in fetchedDocuments yet
  // This can happen when a match is just created and the query hasn't returned the matched document
  const orphanedMatches = useMemo(() => {
    // Check if manual/clay matches are already in query results
    const manualMatchIds = new Set(
      [...fetchedManualMatches, ...fetchedClayMatches]
        .map(m => m.settleId || m.id)
        .filter(Boolean)
    );

    return matchesFromDocument
      .filter(match => {
        const matchId = match.id || match.settleId;
        // Only include matches that:
        // 1. Have an ID
        // 2. Are not already in fetchedTransactions (by ID)
        // 3. Are not already in fetchedDocuments (by ID)
        // 4. Are not already in manual/clay query results (by ID)
        // 5. Have type TAX_DOCUMENT (document-to-document matches) OR type MANUAL (manual/clay matches)
        if (!matchId) return false;
        const isInTransactions = fetchedTransactions.some(t => t.id === matchId);
        const isInDocuments = fetchedDocumentsMap[matchId];
        const isInManualMatches = manualMatchIds.has(matchId);
        const isTaxDocumentMatch = match.type === 'TAX_DOCUMENT';
        const isObligationMatch = match.type === 'OBLIGATION';
        const isManualMatch = match.type === 'MANUAL';
        return !isInTransactions && !isInDocuments && !isInManualMatches && (isTaxDocumentMatch || isObligationMatch || isManualMatch);
      })
      .map(match => {
        // Handle MANUAL matches differently
        if (match.type === 'MANUAL') {
          return {
            id: match.id || match.settleId,
            settleId: match.settleId,
            amount: match.amount || match.matchedAmount || 0,
            description: getMatchDescription(match),
            documentDate: editData?.documentDate,
            date: editData?.documentDate,
            currencyType: match.currency || match.currencyType || editData?.currencyType || 'CLP',
            type: 'MANUAL',
            origin: match.origin || 'manual',
            userRelated: match.userRelated,
            documentType: null,
            matchedAmount: match.amount || match.matchedAmount || 0,
            currency: match.currency || match.currencyType || 'CLP',
            matchSource: 'manual',
            entries: match.entries,
            meta: {
              summary: getMatchDescription(match),
              isReceived: editData?.meta?.isReceived !== undefined ? editData.meta.isReceived : true,
            },
            _isOrphanedMatch: true,
          };
        }

        // Handle TAX_DOCUMENT matches (existing logic)
        const summary = match.obligationType || 'Documento Tributario';
        return {
          id: match.id || match.settleId,
          documentType: match.type || 'TAX_DOCUMENT',
          amount: match.amount || match.matchedAmount || 0,
          currencyType: match.currency || match.matchCurrency || 'CLP',
          documentDate: match.documentDate || new Date().toISOString(),
          name: match.obligationType || 'Document',
          description: '',
          // Add meta object with required fields for matchRowHelpers
          meta: {
            summary: summary,
            documentId: match.documentId || null,
            isReceived: match.isReceived !== undefined ? match.isReceived : true,
            issuerName: match.issuerName || null,
            issuerId: match.issuerId || null,
            receiverName: match.receiverName || null,
            receiverId: match.receiverId || null,
            remaining: match.remaining !== undefined ? match.remaining : null,
          },
          // Include match-specific data
          matchedAmount: match.matchedAmount || match.amount || 0,
          userRelated: match.userRelated,
          currency: match.currency || match.matchCurrency || 'CLP',
          originalAmount: match.originalAmount,
          exchangeRate: match.exchangeRate,
          localValue: match.localValue || match.matchedAmount || match.amount,
          matchCurrency: match.matchCurrency || 'CLP',
          matchSource: match.matchSource,
          origin: match.origin,
          entries: match.entries,
          // Mark as orphaned so we know it needs full data
          _isOrphanedMatch: true,
        };
      });
  }, [matchesFromDocument, fetchedTransactions, fetchedDocumentsMap, fetchedManualMatches, fetchedClayMatches, editData]);

  // Combine transaction, obligation, tax document, manual/clay, and orphaned matches
  const enrichedMatches = useMemo(() => {
    return [
      ...enrichedTransactionMatches,
      ...enrichedObligationMatches,
      ...enrichedTaxDocumentMatches,
      ...formattedManualMatches,
      ...orphanedMatches,
    ];
  }, [enrichedTransactionMatches, enrichedObligationMatches, enrichedTaxDocumentMatches, formattedManualMatches, orphanedMatches]);

  // Transform matches to the format expected by MatchList
  const matchsData = useMemo(() => {
    if (enrichedMatches.length > 0) {
      return {
        allMatchsByTransactionId: [],
        allMatchManualByTransactionId: [],
        allMatchByClay: [],
        allTransactionsByDocumentId: enrichedMatches,
      };
    }
    return null;
  }, [enrichedMatches]);

  const { undoAll, isUndoing, job, percent, cancel, retry, reset } = useUndoAllMatches({
    editData,
    isDocument: true,
    companyId,
    matches: enrichedMatches,
  });
  const {
    summary: statementUnmatchSummary,
    setSummary: setStatementUnmatchSummary,
    findBlockedSummary,
    checking: checkingStatementUnmatch,
  } = useStatementUnmatchGuard(companyId);

  // Only show spinner on initial load (when there's no data yet), not when switching documents
  const shouldShowSpinner = useMemo(() => {
    // Only show spinner if we're fetching AND we don't have any data yet
    return fetchingMatches && !matchesData;
  }, [fetchingMatches, matchesData]);

  // Build match container for amount lookups
  // IMPORTANT: When displaying in CLP, use localValue when available (it is always in CLP).
  const matchContainer = useMemo(() => {
    return matchesFromDocument.reduce(
      (acc, el) => {
        if (!acc) acc = {};
        const matchAmount = el.matchedAmount != null && el.matchedAmount !== undefined
          ? el.matchedAmount
          : (el.localValue || el.localAmount || el.amount || 0);
        const matchCurrency = el.matchCurrency || 'CLP';
        const clpAmount = el.localValue ?? el.localAmount;
        const displayAmount =
          matchCurrency === 'CLP' &&
          el.currency !== 'CLP' &&
          (clpAmount != null && clpAmount !== undefined)
            ? clpAmount
            : el.currency === 'CLP'
              ? matchAmount
              : (el.originalAmount ?? matchAmount);
        const matchId = el.matchId || el.id || el.settleId;
        if (matchId) {
          acc[matchId] = displayAmount;
          acc[matchId + "currency"] = matchCurrency;
          acc[matchId + "matchedAmount"] = displayAmount;
        }
        const txKey = el.id || el.settleId;
        if (txKey && String(txKey) !== String(matchId) && acc[txKey] === undefined) {
          acc[txKey] = displayAmount;
          acc[txKey + "currency"] = matchCurrency;
          acc[txKey + "matchedAmount"] = displayAmount;
        }
        return acc;
      },
      {}
    ) || {};
  }, [matchesFromDocument]);

  const documentInMatchs = editData?.meta?.matchs?.inMatch;
  const reconciledAmount = getReconciledAmount(
    editData?.amount,
    editData?.meta?.remaining,
    documentInMatchs,
  );
  const isFullyReconciledFlag = isFullyReconciled(
    editData?.amount,
    editData?.meta?.remaining,
    documentInMatchs,
  );

  const handleDownloadMatches = useCallback(() => {
    if (!editData) return;
    const currency = editData.currencyType || editData.currency || 'CLP';
    const docId = editData.meta?.documentId || editData.meta?.number || editData._id || editData.id || '';
    const docType = editData.meta?.typeLabel || editData.meta?.type || 'obligacion';
    const fileName = `matchs-${docType}-${docId}-${moment().format('YYYY-MM-DD')}.xlsx`;

    const resolveConciliadoPor = (rawEmail) => {
      if (!rawEmail) return '-';
      const lower = String(rawEmail).toLowerCase();
      if (lower === 'bot@smartcfo.cl' || lower === 'sistema@smartcfo.cl') return 'Pascale';
      return rawEmail;
    };

    const matchSourceLabel = {
      manual:                        'Manual',
      automatic_rule:                'Regla automática',
      automatic_heuristic:           'Automático (heurística)',
      automatic_ai:                  'IA (Pascale)',
      automatic_n_1:                 'N abonos → 1 factura',
      automatic_ai_nomina:           'IA (nómina)',
      automatic_iva_postergado:      'IA (IVA postergado)',
      automatic_f29_check:           'IA (F29)',
      automatic_strong_candidate:    'Candidato fuerte',
      automatic_exact_candidates:    'Candidato exacto',
      confirmed_strong_candidate:    'Candidato fuerte (confirmado)',
      confirmed_ai_recommendation:   'Recomendación IA (confirmada)',
      automatic_credit:              'Crédito automático',
      auto_comision:                 'IA (comisión bancaria)',
      automatic_outsource:           'Match Externo',
    };

    const obligacionRow = [{
      'Tipo':           editData.meta?.typeLabel || editData.meta?.type || '-',
      'N° Documento':   editData.meta?.documentId || editData.meta?.number || '-',
      'Fecha':          editData.documentDate ? moment(editData.documentDate).format('DD/MM/YYYY') : '-',
      'Vencimiento':    editData.dueDate ? moment(editData.dueDate).format('DD/MM/YYYY') : '-',
      'Moneda':         currency,
      'Monto Total':    editData.amount ?? '-',
      'Monto Original': editData.originalAmount ?? '-',
      'Saldo':          editData.meta?.remaining ?? '-',
      'Conciliado':     reconciledAmount ?? '-',
      'Pagado':         editData.isPaid ? 'Sí' : 'No',
      'Emisor':         editData.issuerName || editData.meta?.issuerName || '-',
      'RUT Emisor':     editData.issuerId || editData.meta?.issuerId || '-',
      'Receptor':       editData.receiverName || editData.meta?.receiverName || '-',
      'RUT Receptor':   editData.receiverId || editData.meta?.receiverId || '-',
    }];

    const matchRows = enrichedMatches.map(m => {
      const { matchedByEmail, matchCreationDate, matchSource } = getMatchUserInfo(m, editData);
      const isAccounted = Boolean(m.entries?.completed || m.transaction?.entries?.completed);
      const eoafCategory = m.cashflowArea?.name || '-';
      const docLabel = getDocumentLabelWithNumber(m) || '-';
      const descripcion = getName(m) || m.description || '-';
      const matchAmountCurrency = m.currency || m.currencyType || m.matchCurrency || 'CLP';
      const matchAmount = m.matchedAmount ?? m.amount;
      const fechaMovimiento = m.documentDate
        ? moment(m.documentDate).format('DD/MM/YYYY')
        : m.date
          ? moment(m.date).format('DD/MM/YYYY')
          : '-';
      const fechaConciliacion = matchCreationDate
        ? moment(matchCreationDate).format('DD/MM/YYYY HH:mm')
        : '-';

      return {
        'Movimiento / Documento': docLabel,
        'Descripción':            descripcion,
        'Fecha movimiento':       fechaMovimiento,
        'Fecha conciliación':     fechaConciliacion,
        'Moneda':                 matchAmountCurrency,
        'Monto conciliado':       matchAmount ?? '-',
        'Monto original':         m.originalAmount ?? '-',
        'Valor local (CLP)':      m.localValue ?? '-',
        'Tipo de cambio':         m.exchangeRate ?? '-',
        'Conciliado por':         resolveConciliadoPor(matchedByEmail),
        'Método':                 matchSourceLabel[matchSource] || matchSource || 'Manual',
        'Contabilizado':          isAccounted ? 'Sí' : 'No',
        'Categoría EOAF':         eoafCategory,
        'Emisor':                 m.meta?.issuerName || m.issuerName || '-',
        'RUT Emisor':             m.meta?.issuerId || m.issuerId || '-',
        'Receptor':               m.meta?.receiverName || m.receiverName || '-',
        'RUT Receptor':           m.meta?.receiverId || m.receiverId || '-',
      };
    });

    import('xlsx').then(XLSX => {
      const wb = XLSX.utils.book_new();
      const wsMatch = XLSX.utils.json_to_sheet(
        matchRows.length
          ? matchRows
          : [{ 'Sin matchs': 'Esta obligación no tiene matchs registrados' }]
      );
      const matchColCount = matchRows.length > 0 ? Object.keys(matchRows[0]).length : 17;
      wsMatch['!cols'] = Array.from({ length: matchColCount }, (_, i) => {
        if (i === 0) return { wch: 38 };
        if (i === 1) return { wch: 45 };
        if (i === 9) return { wch: 25 }; // Conciliado por
        return { wch: 16 };
      });
      const wsObl = XLSX.utils.json_to_sheet(obligacionRow);
      const oblColCount = Object.keys(obligacionRow[0]).length;
      wsObl['!cols'] = [{ wch: 20 }, { wch: 45 }, ...Array(Math.max(0, oblColCount - 2)).fill({ wch: 16 })];
      XLSX.utils.book_append_sheet(wb, wsMatch, 'Matchs');
      XLSX.utils.book_append_sheet(wb, wsObl, 'Obligación');
      XLSX.writeFile(wb, fileName);
    });
  }, [editData, enrichedMatches, reconciledAmount]);

  if (!editData) {
    return null;
  }

  return (
    <div className="reconcileForm">
      <DocumentInfoBar editData={editData} />
      
      {/* Action Buttons and Conciliado Amount - Hide when hideActionsAndMatches is true */}
      {!hideActionsAndMatches && (
        <>
          <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
            <div className="tw-text-sm tw-text-gray-700 d-flex align-items-center">
              <span className="tw-font-semibold">{dt('reconcile.reconciledLabel')}</span>
              <span style={{ marginLeft: '4px' }}>{formatMoneyWithCurrency(reconciledAmount, editData.currencyType || editData.currency || 'CLP')}</span>
            </div>
            <div className="d-flex agendaSubmitButton align-items-center" style={{ gap: '0.5em', flexShrink: 0, position: 'relative', overflow: 'visible' }}>
              {/* Solo usuarios restringidos o con conciliación bloqueada no ven botones de acción */}
              {!isReadOnly && (
                <>
                  <DocumentActionsDropdown
                    onDownloadMatches={handleDownloadMatches}
                    onUndoMatches={
                      enrichedMatches.length > 0
                        ? () => setShowUndoAllModal(true)
                        : undefined
                    }
                    {...(showObligationActions && {
                      showCopy: showObligationCopyAction && !crearObligacionBlocked,
                      onDelete: () => setShowDeleteObligationModal(true),
                      onCopy: () => {
                        if (!crearObligacionBlocked) setShowCopyObligationModal(true);
                      },
                    })}
                  />
                  {!abonarBlocked && (
                  <PaymentMethodDropdown
                    onSelect={(method) => {
                      const isReceived =
                        editData?.meta?.isReceived === true || editData?.meta?.isReceived === false
                          ? editData.meta.isReceived
                          : true;
                      const obligationType = getObligationTypeForPaymentMethod(method.id, isReceived);

                      if (obligationType) {
                        setPreSelectedObligationType(obligationType);
                        setIsPaymentMethod(true);
                        setShowAbonoObligationModal(true);
                      }
                    }}
                    disabled={isFullyReconciledFlag}
                    isReceived={editData?.meta?.isReceived === true || editData?.meta?.isReceived === false 
                      ? editData.meta.isReceived 
                      : true}
                  />
                  )}
                  <Button
                    type="button"
                    size="mini"
                    className="scfo-btn-conciliar-primary"
                    onClick={() => setShowVincularModal(true)}
                    disabled={isFullyReconciledFlag}
                  >
                    {dt('reconcile.reconcile')}
                    <i className="arrow up right icon" style={{ marginLeft: '0.5em', transform: 'rotate(45deg)' }}></i>
                  </Button>
                </>
              )}
            </div>
          </div>
          
          {/* Horizontal Divider */}
          <Divider className="mb-4" />

          {/* IA: sugerencia de movimientos para explicar factura */}
          <AIMovementSuggestion
            suggestion={aiInvoiceMovementSuggestion}
            invoice={editData}
            readOnly={isReadOnly}
          />
          
          {/* Matches List - Shows transactions that match with this document */}
          <MatchList
            matches={matchsData}
            matchContainer={matchContainer}
            editData={editData}
            isTransitioning={false}
            fetching={shouldShowSpinner}
            history={history}
            location={location}
            strongCandidates={rendicionStrongCandidates}
            weakCandidates={rendicionWeakCandidates}
            readOnly={isReadOnly}
          />
        </>
      )}

      {/* Modals */}
      <VincularDocumentoModal
        open={showVincularModal}
        onClose={() => setShowVincularModal(false)}
        data={editData}
        onSuccess={() => {
          if (documentId) {
            refetchMatches({ requestPolicy: 'network-only' });
          }
        }}
      />

      <CreateObligationModal
        open={showAbonoObligationModal && !abonarBlocked}
        onClose={() => {
          setShowAbonoObligationModal(false);
          setPreSelectedObligationType(null);
          setIsPaymentMethod(false);
        }}
        onSuccess={() => {
          if (documentId) {
            refetchMatches({ requestPolicy: 'network-only' });
          }
        }}
        initialData={editData}
        preSelectedObligationType={preSelectedObligationType}
        isPaymentMethod={isPaymentMethod}
      />

      <CreateObligationModal
        open={showCopyObligationModal && !crearObligacionBlocked}
        obligationCopyMode
        onClose={() => setShowCopyObligationModal(false)}
        onSuccess={() => {
          dispatch(setUpdateDocuments({ value: true }));
          setShowCopyObligationModal(false);
        }}
        initialData={copyObligationInitialData}
        preSelectedObligationType="invoice"
      />

      {showObligationActions && companyId && (
        <ModalDeleteObligations
          open={showDeleteObligationModal}
          onOpenChange={setShowDeleteObligationModal}
          selectedDocuments={obligationDeleteDocuments}
          companyId={companyId}
          onDeleted={() => {
            setShowDeleteObligationModal(false);
            closeInternalPanel();
          }}
        />
      )}

      <CustomModal
        open={showUndoAllModal}
        onClose={() => { if (!isUndoing) setShowUndoAllModal(false); }}
        title={dt('reconcile.undoAllMatchesTitle')}
        size="small"
        dimmer="blurring"
        closeOnDimmerClick={!isUndoing}
        closeOnEscape={!isUndoing}
        classNameModal="delete-obligation-modal"
        classNameContent="delete-obligation-modal-content"
        content={<UndoAllMatchesModalContent count={enrichedMatches.length} />}
        onConfirm={async () => {
          const pairs = (enrichedMatches || [])
            .map((match) => buildUnmatchInput(match, editData, true, companyId))
            .filter(Boolean)
            .map((input) => ({ originId: input.originId, endId: input.endId }));
          const blocked = await findBlockedSummary(pairs);
          if (blocked) {
            setShowUndoAllModal(false);
            setStatementUnmatchSummary(blocked);
            return;
          }
          await undoAll();
          setShowUndoAllModal(false);
        }}
        confirmLabel={dt('reconcile.undoAllMatchesConfirm')}
        confirmDisabled={isUndoing || checkingStatementUnmatch}
        isLoading={isUndoing || checkingStatementUnmatch}
        confirmVariant="danger"
        cancelarLabel={dt('common.cancel')}
      />

      <StatementLinkedUnmatchModal
        open={Boolean(statementUnmatchSummary)}
        summary={statementUnmatchSummary}
        onClose={() => setStatementUnmatchSummary(null)}
      />

      <BulkUnmatchProgress
        job={job}
        percent={percent}
        onCancel={cancel}
        onRetry={retry}
        onClose={reset}
      />

      {/* Report Button - hidden when used inside CreateJournalEntryModal */}
      {!hideReportButton && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', borderTop: '1px solid #e0e0e0', marginTop: '16px' }}>
          <ReportDocumentButton data={editData} />
        </div>
      )}
    </div>
  );
};

export default ReconcileDocumentSideBar;

